import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { authService } from '../../services';
import { Button, Input, Card, CardBody } from '../../components/ui';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const token = searchParams.get('token');

  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const password = watch('password');

  useEffect(() => {
    if (!token) {
      toast.error(t('auth.invalidToken'));
      navigate('/forgot-password');
    }
  }, [token, navigate, t]);

  const onSubmit = async (data) => {
    if (!token) {
      toast.error(t('auth.invalidToken'));
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, data.password);
      setSuccess(true);
      toast.success(t('auth.passwordResetSuccess'));
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{t('auth.resetPassword')} - ProCTM</title>
      </Helmet>

      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-neutral-dark">{t('auth.resetPassword')}</h1>
            <p className="mt-2 text-gray-500">
              {success ? t('auth.passwordResetSuccess') : t('auth.enterNewPassword')}
            </p>
          </div>

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-8 h-8 text-success" />
              </div>
              <h2 className="text-lg font-semibold text-neutral-dark mb-2">
                {t('auth.passwordResetSuccess')}
              </h2>
              <p className="text-gray-500 mb-6">
                {t('auth.passwordResetSuccessDesc')}
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                {t('auth.signIn')}
              </Button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="relative">
                  <Input
                    label={t('auth.newPassword')}
                    type={showPassword ? 'text' : 'password'}
                    // icon={LockClosedIcon}
                    error={errors.password?.message}
                    placeholder={t('auth.passwordPlaceholder') || '••••••••'}
                    {...register('password', {
                      required: t('errors.required'),
                      minLength: { value: 8, message: t('errors.minLength', { min: 8 }) }
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <div className="relative">
                  <Input
                    label={t('auth.confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    // icon={LockClosedIcon}
                    error={errors.confirmPassword?.message}
                    placeholder={t('auth.passwordPlaceholder') || '••••••••'}
                    {...register('confirmPassword', {
                      required: t('errors.required'),
                      validate: value => value === password || t('auth.passwordMismatch')
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <Button type="submit" className="w-full" loading={loading}>
                  {t('auth.resetPassword')}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-500">
                <Link to="/login" className="text-primary font-medium hover:underline">
                  {t('auth.backToLogin')}
                </Link>
              </p>
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
}
